import { useEffect } from "react"

import { OverrideMode, useVariantOverrides } from "./useVariantOverrides"
import { useVariants } from "./useVariants"
import { Client } from "../client"
import { getVariantFromFallback } from "../helpers/getVariantFromFallback"
import type { Fallback, Variant } from "../types"

export type UseVariantOptions<TPayload> = {
  fallback: Fallback<TPayload>
  disableExposureEvent?: boolean
}

export const useVariant = <TPayload>(
  name: string,
  fallbackOrOptions: Fallback<TPayload> | UseVariantOptions<TPayload>,
): Variant<TPayload> => {
  const isOptionsObject = typeof fallbackOrOptions === "object" && "fallback" in fallbackOrOptions

  const fallback = getVariantFromFallback(isOptionsObject ? fallbackOrOptions.fallback : fallbackOrOptions)

  const options = isOptionsObject ? fallbackOrOptions : undefined
  const disableExposureEvent = !!options?.disableExposureEvent

  const storedVariant = useVariants(state => state.variants[name])

  const override = useVariantOverrides(state => state.overrides[name])
  const overrideMode = useVariantOverrides.useMode()
  const hasOverride = !!override

  // Track exposure and sync
  useEffect(() => {
    // No need to track or sync anything if we're using an override for this flag
    if (hasOverride) return

    // Calling `getVariant` automatically triggers an exposure event
    if (disableExposureEvent) return
    const clientVariant = Client.getVariant<TPayload>(name, fallback)

    // Sync changes to the variants store if the variant is outdated
    if (clientVariant.value === storedVariant?.value) return
    useVariants.getState().setVariant(name, clientVariant)
  }, [disableExposureEvent, fallback, hasOverride, name, storedVariant?.value])

  if (overrideMode === OverrideMode.OverridesOnly) {
    return override ?? fallback
  }

  return override ?? storedVariant ?? fallback
}
